import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import PostDetailsUI from '../view/PostDetailsUI';
import PostsStore from '../../store/PostsStore';
import {
  POST_CAPTION_MAX_LENGTH,
  PostListItem,
  PostMediaItem,
} from '../../../../data/models/Posts/Post';
import {Course} from '../../../../data/models/Course/Course';
import {LessonContentList} from '../../../../data/models/LessonContent/LessonContentList';
import ModalFade, {
  useModalFade,
} from '../../../Microreels/List/components/ModalFade';
import EditMicroreelInfoModal from '../../../Microreels/List/components/EditMicroreelInfoModal';
import MicroreelLearnersModal from '../../../Microreels/List/components/MicroreelLearnersModal';
import ConfirmationModal from '../../../../uiToolkit/ConfirmationModal';
import RenamePostMediaModal from '../../components/RenamePostMediaModal';
import {readPostContent, renamePostNode} from '../../postContent';
import {postsText} from '../../postsText';
import {postPosterText} from '../../postPosterText';

interface Props {
  postsStore?: PostsStore;
}

const PostDetailsContainer = inject('postsStore')(
  observer(({postsStore}: Props) => {
    const [post, setPost] = useState<PostListItem>();
    const [course, setCourse] = useState<Course>();
    const [items, setItems] = useState<PostMediaItem[]>([]);
    const [internalIds, setInternalIds] = useState<string[]>([]);
    const [content, setContent] = useState<LessonContentList>();
    const [editShown, setEditShown] = useState(false);
    const [learnersShown, setLearnersShown] = useState(false);
    const [itemToRename, setItemToRename] = useState<PostMediaItem>();
    const [itemToRemove, setItemToRemove] = useState<PostMediaItem>();
    const [busyIndexes, setBusyIndexes] = useState<number[]>([]);
    const {organizationId, postId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const editFade = useModalFade(editShown);
    const renameFade = useModalFade(!!itemToRename);
    const removeFade = useModalFade(!!itemToRemove);

    const loadContent = async (lessonId: string) => {
      if (!organizationId) return;
      const loaded = await postsStore!.getPostContent(organizationId, lessonId);
      if (!loaded) return;
      const parsed = readPostContent(loaded);
      setContent(loaded);
      setItems(parsed);
      setInternalIds(parsed.map(item => item.id));
    };

    const loadPost = async (isActive: () => boolean = () => true) => {
      if (!organizationId || !postId) return;
      const found = await postsStore!.getPostById(organizationId, postId);
      if (!isActive()) return;
      if (!found) {
        navigate(`/manage/${organizationId}/posts`, {replace: true});
        return;
      }
      setPost(found);
      if (found.lessonId) {
        await loadContent(found.lessonId);
      }
    };

    useEffect(() => {
      let active = true;
      loadPost(() => active);
      return () => {
        active = false;
      };
    }, [organizationId, postId]);

    useEffect(() => {
      if (!organizationId || !editShown || !postId) return;
      postsStore!.getPostCourse(organizationId, postId).then(loaded => {
        if (!loaded) {
          setEditShown(false);
          return;
        }
        setCourse(loaded);
      });
    }, [editShown, organizationId, postId]);

    const onMoveItem = async (index: number, direction: 1 | -1) => {
      if (!organizationId || !post?.lessonId || busyIndexes.length) {
        return;
      }
      const target = index + direction;
      if (target < 0 || target >= internalIds.length) return;

      const ordered = [...internalIds];
      const [moved] = ordered.splice(index, 1);
      ordered.splice(target, 0, moved);

      setBusyIndexes([index, target]);
      const success = await postsStore!.reorderPostMedia(
        organizationId,
        post.id,
        post.lessonId,
        ordered,
      );
      if (success) {
        await loadContent(post.lessonId);
      }
      setBusyIndexes([]);
    };

    const onSaveInfo = async (
      name: string,
      description: string,
      cover?: File,
    ) => {
      if (!organizationId || !course) return;
      const success = await postsStore!.editPost(
        organizationId,
        {...course, title: name, description},
        post?.lessonId,
        cover,
      );
      if (success) {
        setEditShown(false);
        loadPost();
      }
    };

    const onSaveItemName = async (name: string) => {
      if (!organizationId || !post?.lessonId || !itemToRename || !content) {
        return;
      }
      const node = renamePostNode(content, itemToRename.id, name);
      const index = internalIds.indexOf(itemToRename.id);
      if (!node || index < 0) return;

      setItemToRename(undefined);
      setBusyIndexes([index]);
      const success = await postsStore!.renamePostMedia(
        organizationId,
        post.id,
        post.lessonId,
        node,
      );
      if (success) {
        await loadContent(post.lessonId);
      }
      setBusyIndexes([]);
    };

    const onConfirmRemoveItem = async () => {
      if (!organizationId || !post?.lessonId || !itemToRemove) return;
      const index = internalIds.indexOf(itemToRemove.id);
      if (index < 0) return;

      setItemToRemove(undefined);
      setBusyIndexes([index]);
      await postsStore!.removePostMedia(
        organizationId,
        post.id,
        post.lessonId,
        internalIds,
        index,
      );
      // The first item is reordered before it is removed, so the run can have
      // changed even when the removal itself failed.
      await loadContent(post.lessonId);
      setBusyIndexes([]);
    };

    const onAnalytics = () => {
      if (!post?.publishedCourseId) return;
      navigate(
        `/manage/${organizationId}/analytics/course/${post.publishedCourseId}/learners`,
        {state: {backPath: `/manage/${organizationId}/posts/${post.id}`}},
      );
    };

    return (
      <>
        <PostDetailsUI
          post={post}
          items={items}
          isLoading={postsStore!.isLoading}
          onBack={() =>
            navigate(
              (location.state as {backPath?: string} | null)?.backPath ??
                `/manage/${organizationId}/posts`,
            )
          }
          onAnalytics={onAnalytics}
          onEditInfo={() => setEditShown(true)}
          onLearners={() => setLearnersShown(true)}
          onMoveItem={onMoveItem}
          onRenameItem={setItemToRename}
          onRemoveItem={setItemToRemove}
          busyIndexes={busyIndexes}
        />
        {editFade.rendered && (
          <ModalFade closing={editFade.closing}>
            <EditMicroreelInfoModal
              shown
              headerTitle={postsText.editInfoTitle}
              initialName={course?.title}
              initialDescription={course?.description}
              isLoading={postsStore!.isEditLoading}
              onCancel={() => setEditShown(false)}
              namePlaceholder={postsText.postTitlePlaceholder}
              descriptionField={postsText.caption}
              descriptionPlaceholder={postsText.captionPlaceholder}
              maxDescriptionLength={POST_CAPTION_MAX_LENGTH}
              posterText={postPosterText}
              posterAspectRatio="9 / 16"
              posterMaxWidth={190}
              initialCover={post?.poster}
              onSave={onSaveInfo}
            />
          </ModalFade>
        )}
        {renameFade.rendered && (
          <ModalFade closing={renameFade.closing}>
            <RenamePostMediaModal
              initialName={itemToRename?.title}
              isLoading={postsStore!.isEditLoading}
              onCancel={() => setItemToRename(undefined)}
              onSave={onSaveItemName}
            />
          </ModalFade>
        )}
        {removeFade.rendered && (
          <ModalFade closing={removeFade.closing}>
            <ConfirmationModal
              shown
              title={postsText.removeMediaConfirm}
              acceptText={postsText.remove}
              onAccept={onConfirmRemoveItem}
              onCancel={() => setItemToRemove(undefined)}
            />
          </ModalFade>
        )}
        {learnersShown && post?.publishedCourseId && (
          <MicroreelLearnersModal
            groupTitle={post.title}
            publishedCourseId={post.publishedCourseId}
            onClose={() => setLearnersShown(false)}
          />
        )}
      </>
    );
  }),
);

export default PostDetailsContainer;
